import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Search,
  ArrowUpDown,
  ScrollText,
} from "lucide-react";
import { api } from "../../utils/api";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatValue(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

const HIDDEN_KEYS = new Set([
  "id",
  "at",
  "type",
  "tenantId",
  "propertyId",
  "tenantName",
]);

function eventDetails(event) {
  return Object.entries(event)
    .filter(([k]) => !HIDDEN_KEYS.has(k))
    .map(([k, v]) => ({ key: k, value: formatValue(v) }));
}

export default function AdminUserEvents() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState("desc"); // desc | asc
  const [typeFilter, setTypeFilter] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    setLoading(true);
    api
      .get(`/admin/users/${id}/events`)
      .then((data) => setEvents(data.events || []))
      .catch((err) => setError(err.message || "Failed to load events"))
      .finally(() => setLoading(false));
  }, [id]);

  const eventTypes = useMemo(() => {
    const set = new Set();
    events.forEach((e) => e.type && set.add(e.type));
    return Array.from(set).sort();
  }, [events]);

  const processed = useMemo(() => {
    let list = events;
    if (typeFilter) list = list.filter((e) => e.type === typeFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((e) => {
        const hay = JSON.stringify(e).toLowerCase();
        return hay.includes(q);
      });
    }
    list = [...list].sort((a, b) => {
      const ta = a.at || "";
      const tb = b.at || "";
      return order === "asc" ? ta.localeCompare(tb) : tb.localeCompare(ta);
    });
    return list;
  }, [events, typeFilter, search, order]);

  const toggle = (eid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(eid)) next.delete(eid);
      else next.add(eid);
      return next;
    });
  };

  return (
    <div className="space-y-5">
      <button
        onClick={() => navigate(`/admin/users/${id}`)}
        className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" /> Back to user
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-gray-900 dark:text-white">
            <ScrollText className="h-6 w-6 text-indigo-600" />
            Events
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {events.length} total · {processed.length} shown
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search events"
            className="input-base pl-9"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="input-base h-10 max-w-[200px] text-sm"
        >
          <option value="">All types</option>
          {eventTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <button
          onClick={() => setOrder((o) => (o === "asc" ? "desc" : "asc"))}
          className="btn-secondary"
          title={order === "asc" ? "Oldest first" : "Newest first"}
        >
          <ArrowUpDown className="h-4 w-4" />
          {order === "asc" ? "Oldest first" : "Newest first"}
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      ) : processed.length === 0 ? (
        <div className="card p-10 text-center text-sm text-gray-500">
          No events match your filters.
        </div>
      ) : (
        <ul className="card space-y-0 divide-y divide-gray-100 p-0 dark:divide-gray-800">
          {processed.map((e) => {
            const isOpen = expanded.has(e.id);
            const details = eventDetails(e);
            return (
              <li key={e.id} className="p-4">
                <button
                  onClick={() => toggle(e.id)}
                  className="flex w-full flex-wrap items-start justify-between gap-3 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 ring-1 ring-indigo-600/20 dark:bg-indigo-950 dark:text-indigo-300">
                        {e.type || "event"}
                      </span>
                      {e.tenantName && (
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {e.tenantName}
                        </span>
                      )}
                      {e.houseNumber && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          · House {e.houseNumber}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                    {fmtDateTime(e.at)}
                  </div>
                </button>
                {isOpen && details.length > 0 && (
                  <div className="mt-3 rounded-lg bg-gray-50 p-3 text-xs dark:bg-gray-900">
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
                      {details.map((d) => (
                        <div key={d.key} className="flex gap-2">
                          <dt className="font-medium text-gray-500 dark:text-gray-400">
                            {d.key}
                          </dt>
                          <dd className="flex-1 break-words text-gray-800 dark:text-gray-200">
                            {d.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="text-right">
        <Link
          to={`/admin/users/${id}`}
          className="text-xs font-medium text-indigo-600 hover:underline"
        >
          ← Back to user detail
        </Link>
      </div>
    </div>
  );
}
